import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import SvgMapper from "../features/common/SvgMapper";
import "../features/common/Style.css";
import { formatTime, convertDate } from "../utility/common";
import { getAdminSurveyFeedback, createAdminSurvey } from "../../application/services/reportEvaluationService";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const formatReadableDate = (iso) => {
  if (!iso || !iso.includes("T")) return "";
  const [date] = iso.replace("Z", "").split("T")[0].split(".");
  if (!date) return "";
  const [year, month, day] = date.split("-");
  const m = parseInt(month, 10) - 1;
  return day && month && year ? `${parseInt(day, 10)} ${MONTHS[m] || month} ${year}` : "";
};

const TABLE_HEAD = [
  "Student Name",
  "Interview Type",
  "Start At",
  "End At",
  "Report",
  "Rubric",
  "Recording",
  "Actions",
];

const FEEDBACK_TRIM_LENGTH = 120;

const truncateFeedback = (str, maxLen = FEEDBACK_TRIM_LENGTH) => {
  if (str == null || str === "") return { display: "—", title: "" };
  const s = String(str).trim();
  if (s.length <= maxLen) return { display: s, title: "" };
  return { display: `${s.slice(0, maxLen)}…`, title: s };
};

const FeedbackText = ({ value, className = "" }) => {
  const { display, title } = truncateFeedback(value);
  if (!title) {
    return <span className={className}>{display}</span>;
  }
  return (
    <span className={`relative inline-block group ${className}`}>
      {display}
      <span
        className="absolute left-0 bottom-full z-[9999] mb-1 hidden group-hover:block min-w-[16rem] max-w-xl max-h-40 overflow-y-auto overflow-x-hidden py-2 px-3 text-xs font-normal text-gray-100 bg-gray-800 rounded shadow-lg border border-gray-600 whitespace-pre-wrap break-words custom-scrollbar"
        role="tooltip"
      >
        {title}
      </span>
    </span>
  );
};

const StarRating = ({ value, max = 5 }) => {
  const v = Math.min(max, Math.max(0, Number(value) || 0));
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`Rating: ${v} out of ${max}`}>
      {Array.from({ length: max }, (_, i) => (
        <span
          key={i}
          className={i < v ? "text-amber-400" : "text-gray-200"}
          style={{ fontSize: "1rem" }}
        >
          ★
        </span>
      ))}
      <span className="ml-1 text-sm font-medium text-gray-700">{v}</span>
    </span>
  );
};

const ReportTable = ({ reports, loading, error, onAddReport, feedbackByInterviewId = {} }) => {
  const adminEmail = useSelector((state) => state.login?.admin?.user?.email) ?? "";
  const [openIndexes, setOpenIndexes] = useState(new Set());
  const [feedbackOpenGroupIndex, setFeedbackOpenGroupIndex] = useState(null);
  const [adminFeedbackOpenGroupIndex, setAdminFeedbackOpenGroupIndex] = useState(null);
  const [adminFeedbackMap, setAdminFeedbackMap] = useState({});
  const [generateSurveyInterviewId, setGenerateSurveyInterviewId] = useState(null);
  const [surveyForm, setSurveyForm] = useState({
    report_eval_feedback: "",
    question_gen_feedback: "",
    overall_feedback: "",
  });
  const [surveySubmitting, setSurveySubmitting] = useState(false);
  const [surveyError, setSurveyError] = useState(null);

  const groups = Array.isArray(reports) ? reports : [];

  useEffect(() => {
    const toFetch = [];
    groups.forEach((group, groupIndex) => {
      if (!openIndexes.has(groupIndex)) return;
      const firstReport = group?.[0];
      const id = firstReport?.interview_id ?? firstReport?.interviewId;
      if (id && adminFeedbackMap[id] === undefined) toFetch.push(id);
    });
    if (toFetch.length === 0) return;
    Promise.all(
      toFetch.map((id) =>
        getAdminSurveyFeedback(id)
          .then((data) => ({ id, data }))
          .catch(() => ({ id, data: null }))
      )
    ).then((results) => {
      setAdminFeedbackMap((prev) => ({
        ...prev,
        ...Object.fromEntries(results.map((r) => [r.id, r.data])),
      }));
    });
  }, [openIndexes, groups, adminFeedbackMap]);

  const toggleFeedbackGroup = (groupIndex) => {
    setFeedbackOpenGroupIndex((prev) => (prev === groupIndex ? null : groupIndex));
  };

  const toggleAdminFeedbackGroup = (groupIndex) => {
    setAdminFeedbackOpenGroupIndex((prev) => (prev === groupIndex ? null : groupIndex));
  };

  const openGenerateSurvey = (interviewId) => {
    setGenerateSurveyInterviewId(interviewId);
    setSurveyForm({ report_eval_feedback: "", question_gen_feedback: "", overall_feedback: "" });
    setSurveyError(null);
  };

  const closeGenerateSurvey = () => {
    setGenerateSurveyInterviewId(null);
    setSurveyError(null);
  };

  const handleCreateSurvey = async (interviewId) => {
    setSurveySubmitting(true);
    setSurveyError(null);
    try {
      const payload = {
        interview_id: interviewId,
        report_eval_feedback: surveyForm.report_eval_feedback || "",
        question_gen_feedback: surveyForm.question_gen_feedback || "",
        overall_feedback: surveyForm.overall_feedback || "",
        created_by: adminEmail || "email",
      };
      const data = await createAdminSurvey(payload);
      setAdminFeedbackMap((prev) => ({ ...prev, [interviewId]: data }));
      closeGenerateSurvey();
    } catch (err) {
      setSurveyError(err?.message || "Failed to create admin survey.");
    } finally {
      setSurveySubmitting(false);
    }
  };

  const toggleAccordion = (index) => {
    setOpenIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-center p-4">Error: {error}</div>;
  }

  const isEmpty = groups.length === 0 || groups.every((g) => !Array.isArray(g) || g.length === 0);

  if (isEmpty) {
    return (
      <div className="text-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
        <div className="flex justify-center mb-4">
          {SvgMapper.document("w-12 h-12 text-gray-300")}
        </div>
        <p className="text-gray-500">
          No reports found. Upload a report to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {groups.map((group, groupIndex) => {
        const reportsInGroup = Array.isArray(group) ? group : [];
        const firstReport = reportsInGroup[0];
        const studentName = firstReport?.student_name || "N/A";
        const interviewName = firstReport?.interview_name || "";
        const interviewType = firstReport?.interview_type || "";
        const startDate = firstReport?.start_time ? formatReadableDate(firstReport.start_time) : "";
        const startTime = firstReport?.start_time ? formatTime(firstReport.start_time) : "";
        const endDate = firstReport?.end_time ? formatReadableDate(firstReport.end_time) : "";
        const endTime = firstReport?.end_time ? formatTime(firstReport.end_time) : "";
        const sameDate = startDate && endDate && startDate === endDate;
        const reportCount = reportsInGroup.length;
        const isOpen = openIndexes.has(groupIndex);

        return (
          <div
            key={groupIndex}
            className={`rounded-xl border transition-all duration-200 ${isOpen ? "overflow-visible border-blue-200 shadow-md bg-white" : "overflow-hidden border-gray-200 shadow-sm hover:border-gray-300 hover:shadow"}`}
          >
            <button
              type="button"
              onClick={() => toggleAccordion(groupIndex)}
              className="w-full flex items-center justify-between gap-6 px-5 py-4 text-left transition-colors hover:bg-gray-50/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200 focus-visible:ring-offset-1 rounded-t-xl group"
            >
              <div className="flex-1 min-w-0 flex items-center gap-4">
                <div className="flex items-center justify-center w-11 h-11 rounded-full bg-blue-50 text-blue-600 flex-shrink-0 ring-1 ring-blue-100/60">
                  {SvgMapper.user("w-5 h-5")}
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="font-semibold text-gray-900 truncate text-base">{studentName}</p>
                  <p className="text-sm text-gray-500 truncate flex flex-wrap items-center gap-x-2 gap-y-1">
                    {interviewName && <span>{interviewName}</span>}
                    {interviewType && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-teal-100 text-teal-800">
                        {interviewType}
                      </span>
                    )}
                    {(interviewName || interviewType) && (startDate || endDate) && <span>·</span>}
                    {(startDate || endDate) && (
                      sameDate
                        ? `${startDate}${startTime && endTime ? ` ${startTime}–${endTime}` : ""}`
                        : `${startDate} ${startTime || ""} → ${endDate} ${endTime || ""}`
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                  {reportCount} {reportCount === 1 ? "report" : "reports"}
                </span>
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 text-gray-500 transition-all duration-200 group-hover:bg-gray-200">
                  <svg
                    className={`w-5 h-5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-gray-100 bg-gray-50/60">
                {(() => {
                  const firstFeedback = reportsInGroup.reduce((acc, report) => {
                    if (acc) return acc;
                    const id = report.interview_id ?? report.interviewId;
                    return id ? feedbackByInterviewId[id] : null;
                  }, null);
                  const hasFeedback = !!firstFeedback;
                  const isFeedbackOpen = feedbackOpenGroupIndex === groupIndex;
                  return hasFeedback ? (
                    <div className="border-b border-gray-100 bg-white">
                      <div className={`rounded-b-lg border border-t-0 overflow-visible mx-5 mt-0 mb-4 transition-colors ${isFeedbackOpen ? "border-green-200 bg-gray-50/80 shadow-sm" : "border-gray-100 bg-gray-50/80"}`}>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleFeedbackGroup(groupIndex); }}
                          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-100/80 transition-colors"
                        >
                          <span className="flex items-center gap-2 text-sm font-medium text-gray-800">
                            Student Feedback:
                            <StarRating value={firstFeedback.rating} />
                          </span>
                          <span className="flex items-center justify-center w-7 h-7 rounded text-green-600 bg-green-50 hover:bg-green-100 transition-colors">
                            <svg
                              className={`w-4 h-4 transition-transform ${isFeedbackOpen ? "rotate-180" : ""}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </span>
                        </button>
                        {isFeedbackOpen && (
                          <div className="px-4 pb-4 pt-0 space-y-2 border-t border-gray-100 pt-3">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-amber-500 flex-shrink-0" aria-hidden>
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                              </span>
                              <span className="text-gray-600">Rating:</span>
                              <span className="text-gray-900 font-medium">{firstFeedback.rating}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-blue-500 flex-shrink-0" aria-hidden>
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/></svg>
                              </span>
                              <span className="text-gray-600">Comment:</span>
                              <FeedbackText value={firstFeedback.comment} className="text-gray-900" />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null;
                })()}
                {(() => {
                  const interviewId = firstReport?.interview_id ?? firstReport?.interviewId;
                  const adminFeedback = interviewId ? adminFeedbackMap[interviewId] : null;
                  const hasAdminFeedback = !!adminFeedback;
                  const showGenerateSurvey = interviewId && adminFeedbackMap[interviewId] === null;
                  const isAdminFeedbackOpen = adminFeedbackOpenGroupIndex === groupIndex;
                  const isGenerateFormOpen = generateSurveyInterviewId === interviewId;

                  if (hasAdminFeedback) {
                    return (
                      <div className="border-b border-gray-100 bg-white">
                        <div className={`rounded-b-lg border border-t-0 overflow-visible mx-5 mt-0 mb-4 transition-colors ${isAdminFeedbackOpen ? "border-green-200 bg-gray-50/80 shadow-sm" : "border-gray-100 bg-gray-50/80"}`}>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleAdminFeedbackGroup(groupIndex); }}
                            className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-100/80 transition-colors"
                          >
                            <span className="text-sm font-medium text-gray-800">
                              Admin Feedback
                            </span>
                            <span className="flex items-center justify-center w-7 h-7 rounded text-green-600 bg-green-50 hover:bg-green-100 transition-colors">
                              <svg
                                className={`w-4 h-4 transition-transform ${isAdminFeedbackOpen ? "rotate-180" : ""}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </span>
                          </button>
                          {isAdminFeedbackOpen && (
                            <div className="px-4 pb-4 pt-0 space-y-2 border-t border-gray-100 pt-3">
                              <div className="flex items-start gap-2 text-sm">
                                <span className="text-amber-500 flex-shrink-0 mt-0.5" aria-hidden>
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
                                </span>
                                <span className="text-gray-600 shrink-0">Report eval feedback:</span>
                                <FeedbackText value={adminFeedback.report_eval_feedback} className="text-gray-900" />
                              </div>
                              <div className="flex items-start gap-2 text-sm">
                                <span className="flex-shrink-0 mt-0.5 text-[#337265]" aria-hidden>
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M4 6h2v2H4V6zm0 5h2v2H4v-2zm0 5h2v2H4v-2zm3-10h11v2H7V6zm0 5h11v2H7v-2zm0 5h11v2H7v-2z"/></svg>
                                </span>
                                <span className="text-gray-600 shrink-0">Question gen feedback:</span>
                                <FeedbackText value={adminFeedback.question_gen_feedback} className="text-gray-900" />
                              </div>
                              <div className="flex items-start gap-2 text-sm">
                                <span className="text-blue-500 flex-shrink-0 mt-0.5" aria-hidden>
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/></svg>
                                </span>
                                <span className="text-gray-600 shrink-0">Overall feedback:</span>
                                <FeedbackText value={adminFeedback.overall_feedback} className="text-gray-900" />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }

                  if (showGenerateSurvey) {
                    return (
                      <div className="border-b border-gray-100 bg-white">
                        <div className="rounded-b-lg border-2 border-t-0 border-[#337265]/40 bg-[#337265]/5 overflow-visible mx-5 mt-0 mb-4 shadow-sm">
                          <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                            <span className="text-sm font-medium text-[#337265]">No admin feedback</span>
                            {!isGenerateFormOpen ? (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openGenerateSurvey(interviewId); }}
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-[#337265] hover:bg-[#2a5f52] rounded-lg transition-colors"
                              >
                                Generate survey
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); closeGenerateSurvey(); }}
                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                          {isGenerateFormOpen && (
                            <div className="px-4 pb-4 pt-0 border-t border-gray-100 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                              {surveyError && (
                                <p className="text-sm text-red-600">{surveyError}</p>
                              )}
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Report eval feedback</label>
                                <textarea
                                  value={surveyForm.report_eval_feedback}
                                  onChange={(e) => setSurveyForm((f) => ({ ...f, report_eval_feedback: e.target.value }))}
                                  rows={3}
                                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-[#337265]/30 focus:border-[#337265]"
                                  placeholder="Report evaluation feedback"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Question gen feedback</label>
                                <textarea
                                  value={surveyForm.question_gen_feedback}
                                  onChange={(e) => setSurveyForm((f) => ({ ...f, question_gen_feedback: e.target.value }))}
                                  rows={2}
                                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-[#337265]/30 focus:border-[#337265]"
                                  placeholder="Question generation feedback"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Overall feedback</label>
                                <textarea
                                  value={surveyForm.overall_feedback}
                                  onChange={(e) => setSurveyForm((f) => ({ ...f, overall_feedback: e.target.value }))}
                                  rows={2}
                                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-[#337265]/30 focus:border-[#337265]"
                                  placeholder="Overall feedback"
                                />
                              </div>
                              <div className="flex gap-2">
                                {(() => {
                                  const hasAnyFeedback = !!(
                                    (surveyForm.report_eval_feedback || "").trim() ||
                                    (surveyForm.question_gen_feedback || "").trim() ||
                                    (surveyForm.overall_feedback || "").trim()
                                  );
                                  return (
                                    <button
                                      type="button"
                                      onClick={() => handleCreateSurvey(interviewId)}
                                      disabled={surveySubmitting || !hasAnyFeedback}
                                      className="px-4 py-2 text-sm font-medium text-white bg-[#337265] hover:bg-[#2a5f52] rounded-lg disabled:opacity-60 transition-colors disabled:cursor-not-allowed"
                                    >
                                      {surveySubmitting ? "Submitting…" : "Submit"}
                                    </button>
                                  );
                                })()}
                                <button
                                  type="button"
                                  onClick={closeGenerateSurvey}
                                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }

                  return null;
                })()}
                <div className="overflow-x-auto">
                  <table className="w-full min-w-max table-auto text-left">
                    <thead>
                      <tr className="bg-gray-100/80">
                        {TABLE_HEAD.map((head) => (
                          <th
                            key={head}
                            className="px-5 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                          >
                            {head}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {reportsInGroup.map((report, index) => (
                        <tr
                          key={report.id || index}
                          className="hover:bg-blue-50/30 transition-colors"
                        >
                          <td className="px-5 py-4">
                            <span className="text-sm font-medium text-gray-900">
                              {report.student_name || "N/A"}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <span className="text-sm text-gray-600">
                              {report.interview_type || "—"}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <span className="text-sm font-medium text-gray-900 block">
                              {convertDate(report.start_time)}
                            </span>
                            <span className="text-xs text-gray-500 tabular-nums">
                              {formatTime(report.start_time)}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <span className="text-sm font-medium text-gray-900 block">
                              {convertDate(report.end_time)}
                            </span>
                            <span className="text-xs text-gray-500 tabular-nums">
                              {formatTime(report.end_time)}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            {report.admin_report_url ? (
                              <a
                                href={report.admin_report_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                              >
                                {SvgMapper.document("w-4 h-4")}
                                View Report
                              </a>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-5 py-4">
                            {report.rubric_s3_url ? (
                              <a
                                href={report.rubric_s3_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:underline"
                              >
                                {SvgMapper.document("w-4 h-4")}
                                View Rubric
                              </a>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-5 py-4">
                            {report.recording_url ? (
                              <a
                                href={report.recording_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-600 hover:text-amber-700 hover:underline"
                              >
                                {SvgMapper.video("w-4 h-4")}
                                Play Recording
                              </a>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-5 py-4">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onAddReport(report);
                              }}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-green-600 hover:bg-green-50 hover:text-green-700 transition-colors"
                              title={
                                !(report.interview_id ?? report.interviewId) || !report.interview_type
                                  ? "External Report"
                                  : "Internal Report"
                              }
                            >
                              {SvgMapper.plus("w-4 h-4")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ReportTable;
